import { Paper, TableContainer } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router';
import * as api from '../../api/app/emailReportUserApi';
import { AlertDialog } from '../../components/core/AlertDialog';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { DataTable } from '../../components/core/DataTable/DataTable';
import { HeaderCell } from '../../components/core/DataTable/dataTable.types';
import { Loader } from '../../components/core/Loader/Loader';
import { useTranslation } from '../../contexts/translation';
import { useDeleteDialog } from '../../hooks/useDeleteDialog';
import { EmailReportUser } from '../../types/app/emailReportUser.types';
import { dictionary } from '../../utils/core/dictionary';
import { paths } from '../../utils/core/routes';
interface Props {
  isReadOnly: boolean;
}
export function EmailReportUserList({ isReadOnly }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const parentId = Number(id);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const {
    isOpen: isDeleteDialogOpen,
    idToDelete,
    setDeleteAttempt,
    cancelDeleteAttempt,
    confirmDeleteAttempt,
  } = useDeleteDialog();

  const { mutate: mutateDeleteEmailReport } = useMutation(api.deleteEmailReportUser);

  const headerCells: HeaderCell[] = [{ title: 'Name', key: 'userFullName' }];

  const { data, isLoading, isError } = useQuery<EmailReportUser[]>(['emailReportsUser', id], () =>
    api.getEmailReportUserList(Number(id)),
  );

  const handleOnCreate = () =>
    navigate(`${paths.EMAIL_REPORT_LIST}/${parentId}${paths.EMAIL_REPORT_USER_CREATE}`);
  const handleOnDelete = (id: number) => setDeleteAttempt(id);

  const handleConfirm = () => {
    if (!idToDelete) return;

    mutateDeleteEmailReport(idToDelete, {
      onSuccess: () => {
        enqueueSnackbar(dictionary.EMAIL_REPORT_DELETED, { variant: 'success' });
        queryClient.invalidateQueries(['emailReportsUser']);
        confirmDeleteAttempt();
      },
      onError: (result: any) => {
        enqueueSnackbar(`${result}`, { variant: 'error' });
      },
    });
  };

  if (isError) return <ApiError />;
  if (isLoading || !data) return <Loader />;
  return (
    <>
      <TableContainer component={Paper}>
        <DataTable
          headerCells={headerCells}
          items={data}
          onDeleteClick={handleOnDelete}
          onCreateClick={handleOnCreate}
          isReadOnly={isReadOnly}
        />
      </TableContainer>
      <AlertDialog
        open={isDeleteDialogOpen}
        title={t('general.action-buttons.delete')}
        content={t('general.messages.warning.delete-message')}
        handleClose={cancelDeleteAttempt}
        handleConfirm={handleConfirm}
      />
    </>
  );
}
